/**
 * Ручні операції — список (канон Норми #29): .card + InfiniteDocGrid
 * (серверна пагінація) + фільтри рядком під назвою (орг слідує за шапкою,
 * дати з верхнього періоду).
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-community';
import { manualOps, type ManualOperationListItem } from '@/api/documents';
import { orgs } from '@/api/catalogs';
import DatePopover from '@/components/DatePopover';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { useSignControlInfinite } from '@/shared/hooks/useSignControl';
import { useDefaultPeriod, useOrgFilter } from '@/store/defaults';

const fmt = (v: string) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function ManualOperationsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Орг слідує за шапкою до ручного вибору; дати — з верхнього періоду.
  const [orgId, setOrgId] = useOrgFilter();
  const { dateFrom: gFrom, dateTo: gTo } = useDefaultPeriod();
  const [fltFrom, setFltFrom] = useState(gFrom?.slice(0, 10) ?? '');
  const [fltTo, setFltTo] = useState(gTo?.slice(0, 10) ?? '');
  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const orgName = useMemo(() => {
    const m: Record<string, string> = {};
    (orgsQ.data?.items ?? []).forEach((o) => { m[o.id] = o.name; });
    return m;
  }, [orgsQ.data]);

  const { datasource, total } = useInfiniteGridDatasource<ManualOperationListItem>(
    (limit, offset, sort) => manualOps.list({
      organization_id: orgId || undefined,
      date_from: fltFrom || undefined,
      date_to: fltTo || undefined,
      sort_by: sort?.sort_by,
      sort_dir: sort?.sort_dir,
      limit, offset,
    }),
    [orgId, fltFrom, fltTo],
    { attachmentsType: 'doc.manual_operations' },
  );
  // Контроль підписання (0429): колонка «Підпис» + фон непідписаних.
  const sc = useSignControlInfinite<ManualOperationListItem>('doc.manual_operations', orgId);

  const cols = useMemo<ColDef<ManualOperationListItem>[]>(() => [
    {
      field: 'document_number', headerName: t('mo.number'), width: 150,
      cellRenderer: (p: { data?: ManualOperationListItem }) => p.data ? (
        <span>
          {p.data.external_source && (
            <span title="Імпортовано з 1С" style={{
              marginRight: 6, color: '#2563eb', border: '1px solid var(--badge-blue-bd)',
              borderRadius: 4, fontSize: '0.7rem', fontWeight: 700, padding: '0 4px',
            }}>I</span>
          )}
          {p.data.document_number}
        </span>
      ) : '',
    },
    {
      field: 'document_date', headerName: t('mo.date'), width: 150,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString('uk-UA') : '',
    },
    {
      field: 'organization_id', headerName: 'Організація', width: 200,
      valueFormatter: (p) => p.value ? (orgName[p.value] ?? '') : '',
    },
    {
      field: 'operation_content', headerName: t('mo.content'), flex: 1, minWidth: 220,
    },
    {
      field: 'total_amount', headerName: 'Сума', width: 140,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.value != null ? fmt(p.value) : '',
    },
    {
      field: 'posted', headerName: t('mo.posted'), width: 120,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (p) => p.data ? (p.value ? '✓ ' + t('mo.posted_yes') : t('mo.posted_no')) : '',
    },
    {
      colId: '_att', headerName: 'Вкладення', width: 110, sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { _att?: boolean } }) => p.data?._att ? '✓' : '',
    },
  ], [t, orgName]);

  return (
    <div className="card">
      <div className="toolbar with-filters">
        <h2 style={{ margin: 0 }}>{t('mo.title')}<TitleTotal total={total} /></h2>
        <button className="primary" onClick={() => navigate('/manual-operations/new')}>
          + {t('mo.new')}
        </button>
      </div>
      <div className="filters" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', margin: '0 0 0.6rem', alignItems: 'center' }}>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)}
          style={{ maxWidth: 240 }}>
          <option value="">Усі організації</option>
          {(orgsQ.data?.items ?? []).map((o) => (
            <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
          ))}
        </select>
        <div style={{ width: 120, flex: '0 0 auto' }}>
          <DatePopover value={fltFrom} onChange={setFltFrom} />
        </div>
        <span style={{ color: 'var(--muted)' }}>—</span>
        <div style={{ width: 120, flex: '0 0 auto' }}>
          <DatePopover value={fltTo} onChange={setFltTo} />
        </div>
      </div>
      <InfiniteDocGrid<ManualOperationListItem>
        copyRow={(r) => navigate(`/manual-operations/new?copy=${r.id}`)}
        deleteRow={{ remove: (id) => manualOps.remove(id) }}
        datasource={sc.wrap(datasource)}
        columnDefs={[...cols, ...sc.column]}
        storageKey="manual-operations"
        onRowDoubleClicked={(r) => navigate(`/manual-operations/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : sc.getRowStyle(p)}
      />
    </div>
  );
}
